import { Account } from '@/domain/usecases'
import { UserContentSummary } from '@/domain/usecases/content/user-content-summary'
import { UserContentRecommended } from '@/domain/usecases/content/user-content-recommended'
import { ContentRecommendationType } from '@/domain/models/content/content-recommendation-type'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import RegisteredPlanBaseLoader from '../registered-plan-base-loader'

export default class HomeLoader extends RegisteredPlanBaseLoader {
  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined
  ) {
    super(account, queryClient, ldClient)
  }

  async loader() {
    return this.defer({
      data: Promise.all([
        this.getContentSummary(),
        this.getCompanyRecommendation(),
        this.getUserBasedCFRecommendation()
      ]).then(([contentSummary, companyRecommendation, userBasedCFRecommendation]) => ({
        ...contentSummary,
        companyRecommended: companyRecommendation,
        userBasedCFRecommended: userBasedCFRecommendation
      }))
    })
  }

  private async getContentSummary() {
    const makeRemoteUserContentSummary = await super.importRemoteModule(
      'makeRemoteUserContentSummary'
    )
    const remote = makeRemoteUserContentSummary(this.queryClient) as UserContentSummary
    return await remote.get()
  }

  private async getCompanyRecommendation() {
    if (!FeatureFlag.Key.DISPLAY_COMPANY_CONTENT_RECOMMENDATIONS.getValue(this.ldClient)) {
      return Promise.resolve([])
    }

    const recommendationLimit = 3
    const makeRemoteUserContentRecommended = await super.importRemoteModule(
      'makeRemoteUserContentRecommended'
    )
    const remote = makeRemoteUserContentRecommended(this.queryClient) as UserContentRecommended
    return await remote.get(ContentRecommendationType.Enum.COMPANY, recommendationLimit)
  }

  private async getUserBasedCFRecommendation() {
    if (!FeatureFlag.Key.USER_BASED_CF_RECOMMENDATIONS.getValue(this.ldClient)) {
      return Promise.resolve([])
    }

    const recommendationLimit = 4
    const makeRemoteUserContentRecommended = await super.importRemoteModule(
      'makeRemoteUserContentRecommended'
    )
    const remote = makeRemoteUserContentRecommended(this.queryClient) as UserContentRecommended
    return await remote.get(ContentRecommendationType.Enum.USER_BASED_CF, recommendationLimit)
  }
}
